// src/components/HalfCircularTimeline.jsx

import React, { useState, useMemo, useEffect } from "react";
import "./HalfCircularTimeline.css";
import Papa from "papaparse";

// ==============================================
//  NEW, BULLETPROOF UTILITY FUNCTIONS
// ==============================================

const parseDateDetails = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return null;

  const monthNames = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];

  // Create a regex to find any month name, case-insensitively
  const monthRegex = new RegExp(`(${monthNames.join('|')})`, 'i');

  const lowerDateString = dateString.toLowerCase();

  // Find the year (a 4-digit number)
  const yearMatch = lowerDateString.match(/\b\d{4}\b/);
  const year = yearMatch ? parseInt(yearMatch[0], 10) : null;

  // Find the month
  const monthMatch = lowerDateString.match(monthRegex);
  const monthIndex = monthMatch ? monthNames.indexOf(monthMatch[0]) : -1;

  if (year === null || monthIndex === -1) {
    console.warn(`Could not parse year or month from: "${dateString}"`);
    return null;
  }

  // Find the day (the first number in the string)
  const dayMatch = lowerDateString.match(/^\d+/);
  const day = dayMatch ? parseInt(dayMatch[0], 10) : 1; // Default to the 1st of the month if no day is found

  const date = new Date(year, monthIndex, day);
  const hasSpecificDay = !!dayMatch;

  if (isNaN(date.getTime())) {
    console.error(`Failed to create a valid date from: "${dateString}"`);
    return null;
  }

  return { date, day: hasSpecificDay ? day : null, month: monthIndex, year, hasSpecificDay };
};

const isUpcoming = (dateDetails) => {
  if (!dateDetails || !dateDetails.date) return false;

  const { date, hasSpecificDay } = dateDetails;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (hasSpecificDay) {
    // If a day is specified, compare the exact date
    return date >= today;
  } else {
    // If only a month and year are specified, check if the month has passed
    const eventMonthStart = new Date(date.getFullYear(), date.getMonth(), 1);
    const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return eventMonthStart >= todayMonthStart;
  }
};

const ensureAbsoluteUrl = (url) => {
  if (!url) return "#";
  if (url.startsWith('http://') || url.startsWith('https://')) { return url; }
  return `https://${url}`;
};


// ==============================================
//  SUB-COMPONENTS (With minor fixes)
// ==============================================
const MobileCalendar = ({ dateString, registerLink }) => {
  const details = useMemo(() => parseDateDetails(dateString), [dateString]);
  if (!details) return null;
  const { month, year } = details;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return (
    <div className="mobile-calendar-wrapper">
      <div className="mobile-calendar-header">
        <h4>{monthNames[month]} {year}</h4>
        {registerLink && (<a href={ensureAbsoluteUrl(registerLink)} target="_blank" rel="noopener noreferrer" className="register-button-mobile">Register Now</a>)}
      </div>
    </div>
  );
};

const CalendarCard = ({ dateString, registerLink }) => {
  const details = useMemo(() => parseDateDetails(dateString), [dateString]);
  if (!details) return null;
  const { day: eventDay, month, year, hasSpecificDay } = details;
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["S", "M", "T", "W", "T", "F", "S"];
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const blanks = Array(firstDayOfMonth).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const calendarCells = [...blanks, ...days];

  return (
    <div className="calendar-card-desktop">
      <div className="calendar-card-inner">
        <div className="calendar-content-wrapper">
          <div className="calendar-header"><h3>{monthNames[month]} <span>{year}</span></h3></div>
          {hasSpecificDay && (
            <div className="calendar-grid">
              {dayNames.map((d, i) => <div key={i} className="day-name">{d}</div>)}
              {calendarCells.map((d, i) => (
                <div key={i} className={`day-cell ${d === eventDay ? 'highlighted' : ''}`}>{d}</div>
              ))}
            </div>
          )}
          {registerLink && (<a href={ensureAbsoluteUrl(registerLink)} target="_blank" rel="noopener noreferrer" className="register-button">Register Now</a>)}
        </div>
      </div>
    </div>
  );
};

// ==============================================
//      Constants and Main Component
// ==============================================
const SIZE = 1100, HEIGHT = 550, RADIUS = 500, LABEL_SIZE = 70;
const INNER_WIDTH = 600, INNER_HEIGHT = 300;
const VISIBLE = 5, MID = Math.floor(VISIBLE / 2);

const HalfCircularTimeline = () => {
  const [timelineData, setTimelineData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const sheetURL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vTNX1-ninUpER_sVrpJd5caT0Y-P93aqXEjdIcPjnssae2cPS0Z3vzqWuyy2lVZIPYkE0GjOI2A7rsr/pub?output=csv";
    if (!sheetURL) {
      setError("Google Sheet URL is not configured.");
      setIsLoading(false); return;
    }
    Papa.parse(sheetURL, {
      download: true, header: true,
      complete: (results) => {
        const validData = results.data.filter(row => row.id && row.id.trim() !== "");
        if (validData.length > 0) setTimelineData(validData);
        else setError("No valid data found in the sheet.");
        setIsLoading(false);
      },
      error: (err) => {
        console.error("Error fetching sheet:", err);
        setError("Could not load timeline events.");
        setIsLoading(false);
      },
    });
  }, []);

  const hasData = timelineData.length > 0;
  const count = hasData ? timelineData.length : 0;
  const active = hasData ? timelineData[activeIndex] : null;

  const activeEventDetails = useMemo(() => active ? parseDateDetails(active.year) : null, [active]);
  const isUpcomingEvent = useMemo(() => isUpcoming(activeEventDetails), [activeEventDetails]);

  const centerX = SIZE / 2;
  const centerY = HEIGHT;

  const visibleItems = useMemo(() => {
    if (!hasData) return [];
    const step = Math.PI / (VISIBLE - 1);
    const arr = [];
    for (let i = 0; i < VISIBLE; i++) {
      const dataIndex = (activeIndex + (i - MID) + count) % count;
      const item = timelineData[dataIndex];
      if (!item) continue; // Safety check in case of data issues
      const angle = step * i;
      const x = centerX + Math.cos(angle - Math.PI) * RADIUS;
      const y = centerY + Math.sin(angle - Math.PI) * RADIUS;
      arr.push({ ...item, _key: `${item.id}-${dataIndex}`, dataIndex, isActive: dataIndex === activeIndex, x: x - LABEL_SIZE / 2, y: y - LABEL_SIZE / 2 });
    }
    return arr;
  }, [activeIndex, hasData, count, timelineData]);

  if (isLoading) {
    return <section className="timeline-root"><div>Loading events...</div></section>;
  }
  if (error) {
    return <section className="timeline-root"><div style={{ color: 'red' }}>Error: {error}</div></section>;
  }
  if (!active) { // <-- This prevents the crash on initial render
    return <section className="timeline-root"><div>Initializing timeline...</div></section>;
  }

  const goPrev = () => setActiveIndex((i) => (i - 1 + count) % count);
  const goNext = () => setActiveIndex((i) => (i + 1) % count);

  return (
    <section className={`timeline-root ${isUpcomingEvent ? 'is-upcoming' : ''}`}>
      <div className="background-bubbles" aria-hidden="true"><span></span><span></span><span></span><span></span><span></span></div>

      <div className="timeline-display-wrapper-desktop">
        <div className="timeline-main-area" style={{ '--active-bg-image': `url(${active.imagePath})` }}>
          <div className="timeline-path" style={{ width: RADIUS * 2, height: RADIUS * 2, left: centerX - RADIUS, top: centerY - RADIUS }} />
          {visibleItems.map((it) => (
            <button key={it._key} className={`year-label ${it.isActive ? "active" : ""}`} style={{ left: it.x, top: it.y, width: LABEL_SIZE, height: LABEL_SIZE, zIndex: it.isActive ? 10 : 1 }} onClick={() => setActiveIndex(it.dataIndex)}>
              {it.year}
            </button>
          ))}
          <div key={active.id + activeIndex} className="content-card" style={{ width: INNER_WIDTH, height: INNER_HEIGHT, left: centerX - INNER_WIDTH / 2, top: HEIGHT - INNER_HEIGHT - 35 }}>
            <div className="content-text-wrapper"><h2>{active.title}</h2><p>{active.description}</p></div>
          </div>
        </div>
        {isUpcomingEvent && <CalendarCard dateString={active.year} registerLink={active.registerLink} />}
      </div>

      <div className="timeline-display-wrapper-mobile">
        {timelineData.map((item) => {
          const details = parseDateDetails(item.year);
          const isItemUpcoming = isUpcoming(details);
          return (
            <div key={item.id} className="mobile-event-card">
              <div className="mobile-event-image" style={{ '--bg-image': `url(${item.imagePath})` }}></div>
              <div className="mobile-event-content">
                {isItemUpcoming && <span className="upcoming-badge">Upcoming Event</span>}
                <h3 className="mobile-event-year">{item.year}</h3>
                <h2 className="mobile-event-title">{item.title}</h2>
                <p className="mobile-event-description">{item.description}</p>
                {isItemUpcoming && <MobileCalendar dateString={item.year} registerLink={item.registerLink} />}
              </div>
            </div>
          );
        })}
      </div>

      <div className="timeline-controls">
        <button onClick={goPrev} aria-label="Previous Year"> <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor" /> </svg> </button>
        <span className="active-year-display">{active.year}</span>
        <button onClick={goNext} aria-label="Next Year"> <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z" fill="currentColor" /> </svg> </button>
      </div>
    </section>
  );
};

export default HalfCircularTimeline;